import React, { useMemo } from 'react';
import {
    FolderOpenOutlined,
    CheckOutlined,
    SafetyCertificateOutlined,
    FileSearchOutlined,
    AuditOutlined,
    PaperClipOutlined,
    BarChartOutlined,
    ClockCircleOutlined,
    SendOutlined,
    InboxOutlined,
    TeamOutlined,
    CloseCircleOutlined,
    CreditCardOutlined,
    BankOutlined,
    MailOutlined,
    PieChartOutlined,
    CheckSquareOutlined,
    GlobalOutlined,
    EnvironmentOutlined,
    ExclamationCircleOutlined,
    ReloadOutlined,
    StopOutlined,
    FileProtectOutlined,
} from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import { avecLibelleTraduit } from '../i18n/libelles';
import { formatDate, libelleStatutGarantie } from '../variables/constants';

/* ── Charte graphique ── */
const BRAND = {
    green: '#155731',
    greenLight: '#1a6b3c',
    greenBg: '#dcf0e3',
    greenRing: 'rgba(21,87,49,0.15)',
    blue: '#1e40af',
    blueBg: '#dbeafe',
    amber: '#b45309',
    amberBg: '#fef3c7',
    red: '#dc2626',
    redBg: '#fee2e2',
    purple: '#7c3aed',
    purpleBg: '#ede9fe',
    gray: '#6b7280',
    grayBg: '#f3f4f6',
    border: '#e5e7eb',
    textPrimary: '#111827',
    textMuted: '#9ca3af',
};

/* ── Phases & étapes ── */
// Libellés traduits à la lecture : locales/<langue>/commun.json, sinistreStatusStepper.
const ETAPES = 'commun:sinistreStatusStepper.etapes';
const PHASES = avecLibelleTraduit([
    {
        key: 'ouverture',
        color: BRAND.green,
        bg: BRAND.greenBg,
        steps: avecLibelleTraduit([
            { key: 'NOUVEAU', Icon: FolderOpenOutlined },
            { key: 'VALIDE', Icon: CheckOutlined },
            /* Deux statuts, une seule étape : le libellé dépend du type de sinistre
               (ET « Garantie demandée » / TE « En attente de confirmation »), mais
               c'est le même moment du dossier. Leur absence d'ici était le bug :
               l'index du statut courant valait -1 et plus aucune étape ne
               s'allumait - la barre paraissait vide juste après la demande. */
            {
                key: 'GARANTIE_DEMANDEE',
                aussi: ['GARANTIE_ATTENTE_CONFIRMATION'],
                Icon: FileProtectOutlined,
            },
            { key: 'GARANTIE_CONFIRMEE', Icon: SafetyCertificateOutlined },
        ], ETAPES, { cle: 'key' }),
    },
    {
        key: 'instruction',
        color: BRAND.blue,
        bg: BRAND.blueBg,
        steps: avecLibelleTraduit([
            { key: 'ATTENTE_PV', Icon: FileSearchOutlined },
            { key: 'ATTENTE_RC', Icon: AuditOutlined },
            { key: 'ATTENTE_PIECES_DE_RECLAMATION', Icon: PaperClipOutlined },
        ], ETAPES, { cle: 'key' }),
    },
    {
        key: 'evaluation',
        color: BRAND.amber,
        bg: BRAND.amberBg,
        steps: avecLibelleTraduit([
            { key: 'MUR', Icon: BarChartOutlined },
            /* Ordre aligné sur SinistreTransitions : MUR → OFFRE_EFFECTUE →
               ATTENTE_OFFRE → OFFRE_RECUE. L'inversion précédente faisait
               reculer la barre d'un cran à l'envoi de l'offre. */
            { key: 'OFFRE_EFFECTUE', Icon: SendOutlined },
            { key: 'ATTENTE_OFFRE', Icon: ClockCircleOutlined },
            { key: 'OFFRE_RECUE', Icon: InboxOutlined },
        ], ETAPES, { cle: 'key' }),
    },
    {
        key: 'decision',
        color: BRAND.green,
        bg: BRAND.greenBg,
        steps: avecLibelleTraduit([
            { key: 'ACCORD', Icon: TeamOutlined },
            { key: 'REJET', Icon: CloseCircleOutlined },
        ], ETAPES, { cle: 'key' }),
    },
    {
        key: 'reglement',
        color: BRAND.green,
        bg: BRAND.greenBg,
        steps: avecLibelleTraduit([
            { key: 'BAP', Icon: CreditCardOutlined },
            { key: 'ATTENTE_CHEQUE', Icon: BankOutlined },
            { key: 'CHEQUE_RECU', Icon: MailOutlined },
            { key: 'PARTIELLEMENT_PAYE', Icon: PieChartOutlined },
            { key: 'SOLDE', Icon: CheckSquareOutlined },
        ], ETAPES, { cle: 'key' }),
    },
], 'commun:sinistreStatusStepper.phases', { cle: 'key' });

/* ── Statuts hors flux ── */
const STATUTS_SPECIAUX = avecLibelleTraduit({
    /* SOUMISE precede le flux : la declaration n'est engagee dans aucun processus
       metier tant qu'elle n'a pas ete validee (canal mobile) ou affectee (guichet).
       Le libelle reste neutre : le statut ne dit rien du canal. */
    SOUMISE: { color: BRAND.purple, bg: BRAND.purpleBg, Icon: InboxOutlined },
    GARANTIE_REJETEE: { color: BRAND.red, bg: BRAND.redBg, Icon: CloseCircleOutlined },
    GARANTIE_NON_ACQUISE: { color: BRAND.red, bg: BRAND.redBg, Icon: CloseCircleOutlined },
    CLOTURE: { color: BRAND.gray, bg: BRAND.grayBg, Icon: StopOutlined },
    REOUVERT: { color: BRAND.amber, bg: BRAND.amberBg, Icon: ReloadOutlined },
    CONTENTIEUX: { color: BRAND.red, bg: BRAND.redBg, Icon: ExclamationCircleOutlined },
    ARBITRAGE: { color: BRAND.purple, bg: BRAND.purpleBg, Icon: AuditOutlined },
    SANS_SUITE: { color: BRAND.gray, bg: BRAND.grayBg, Icon: StopOutlined },
}, 'commun:sinistreStatusStepper.statutsSpeciaux');

/* ── Type sinistre ── */
const TYPE_SINISTRE = avecLibelleTraduit({
    SURVENU_TOGO: {
        short: 'ET',
        color: BRAND.blue,
        bg: BRAND.blueBg,
        Icon: EnvironmentOutlined,
    },
    SURVENU_ETRANGER: {
        short: 'TE',
        color: BRAND.purple,
        bg: BRAND.purpleBg,
        Icon: GlobalOutlined,
    },
}, 'commun:sinistreStatusStepper.typesSinistre');

const ALL_STEPS = PHASES.flatMap(p => p.steps);
const ALL_KEYS = ALL_STEPS.map(s => s.key);

/** Une etape couvre son `key` et ses `aussi` : index de l'etape portant ce statut, -1 sinon. */
const indexEtape = (statut) =>
    ALL_STEPS.findIndex(s => s.key === statut || (s.aussi || []).includes(statut));

/** Etape portant ce statut, avec la phase qui la contient. */
const etapePourStatut = (statut) => {
    const phase = PHASES.find(p => p.steps.some(s => s.key === statut || (s.aussi || []).includes(statut)));
    const step = phase?.steps.find(s => s.key === statut || (s.aussi || []).includes(statut));
    return { phase, step };
};

/* ── Styles ── */
const S = {
    wrapper: {
        background: '#fff',
        border: `1px solid ${BRAND.border}`,
        borderRadius: 12,
        padding: '20px 24px 16px',
        marginBottom: 20,
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 18,
        flexWrap: 'wrap',
        gap: 10,
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
    },
    title: {
        fontSize: 14,
        fontWeight: 500,
        color: BRAND.textPrimary,
    },
    badge: (color, bg) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 500,
        color,
        background: bg,
        border: `1px solid ${color}22`,
    }),
    typeBadge: (color, bg) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        color,
        background: bg,
        border: `1px solid ${color}33`,
        letterSpacing: '.02em',
    }),
    dot: (color) => ({
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: color,
        display: 'inline-block',
        flexShrink: 0,
    }),
    trackOuter: {
        overflowX: 'auto',
        paddingBottom: 8,
        scrollbarWidth: 'thin',
    },
    trackInner: {
        display: 'flex',
        alignItems: 'flex-start',
        minWidth: 'max-content',
        position: 'relative',
    },
    phaseGroup: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    phaseHeader: (color, bg) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        fontWeight: 600,
        color,
        background: bg,
        padding: '2px 8px',
        borderRadius: 20,
        marginBottom: 10,
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        border: `1px solid ${color}22`,
    }),
    phaseSteps: {
        display: 'flex',
        alignItems: 'flex-start',
    },
    phaseDivider: {
        width: 1,
        height: 56,
        background: BRAND.border,
        margin: '24px 10px 0',
        opacity: 0.7,
        flexShrink: 0,
    },
    stepItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        flexShrink: 0,
        width: 78,
    },
    connector: (done, activeColor) => ({
        position: 'absolute',
        top: 15,
        left: '50%',
        width: '100%',
        height: 2,
        zIndex: 0,
        background: done ? activeColor : BRAND.border,
        transition: 'background .3s',
    }),
    dotWrap: (state, activeColor) => {
        const base = {
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            borderRadius: '50%',
            marginBottom: 8,
            transition: 'all .2s',
        };
        if (state === 'done') return { ...base, background: activeColor };
        if (state === 'active') return { ...base, background: activeColor, boxShadow: `0 0 0 5px ${activeColor}25` };
        return { ...base, background: '#f9fafb', border: `2px solid ${BRAND.border}` };
    },
    stepLabel: (state, activeColor) => ({
        fontSize: 10,
        textAlign: 'center',
        maxWidth: 72,
        lineHeight: 1.35,
        whiteSpace: 'normal',
        color: state === 'active' ? activeColor
            : state === 'done' ? BRAND.textMuted
                : BRAND.gray,
        fontWeight: state === 'active' ? 600 : 400,
    }),
    statsRow: {
        display: 'flex',
        gap: 24,
        marginTop: 16,
        paddingTop: 14,
        borderTop: `1px solid ${BRAND.border}`,
        flexWrap: 'wrap',
    },
    statItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    statValue: (color = BRAND.textPrimary) => ({
        fontSize: 16,
        fontWeight: 600,
        color,
        lineHeight: 1,
    }),
    statLabel: {
        fontSize: 11,
        color: BRAND.textMuted,
    },
    specialBanner: (color, bg) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        borderRadius: 8,
        background: bg,
        border: `1px solid ${color}33`,
        marginBottom: 4,
        color,
        fontSize: 13,
        fontWeight: 500,
    }),
};

/* ══════════════════════════════════════════════ */
export default function SinistreStatusStepper({ sinistre }) {
    const { t } = useTranslation('commun');
    const statut = sinistre?.statut;
    const typeSinistre = sinistre?.typeSinistre;
    const isSpecial = Boolean(statut) && statut in STATUTS_SPECIAUX;
    const currentIdx = indexEtape(statut);

    /* Résolution label + couleur statut courant */
    const { statusColor, statusBg, statusLabel } = useMemo(() => {
        if (isSpecial) {
            const s = STATUTS_SPECIAUX[statut];
            return {
                statusColor: s.color,
                statusBg: s.bg,
                statusLabel: s.label,
            };
        }

        // Recherche de l'etape (et de sa phase) portant le statut, alias compris
        const { phase, step } = etapePourStatut(statut);

        /* Le libellé de l'étape « garantie demandée » dépend du type de sinistre :
           ET → demandée au bureau homologue, TE → demandée à la compagnie. */
        const estEtapeGarantie = statut === 'GARANTIE_DEMANDEE'
            || statut === 'GARANTIE_ATTENTE_CONFIRMATION';

        return {
            statusColor: phase?.color ?? BRAND.green,
            statusBg: phase?.bg ?? BRAND.greenBg,
            statusLabel: estEtapeGarantie
                ? libelleStatutGarantie(statut, typeSinistre)
                : statut === 'ATTENTE_PV' && typeSinistre === 'SURVENU_ETRANGER'
                ? t('sinistreStatusStepper.attentePvHomologue')
                : (step?.label
                    ?? (statut || '-')
                        .replace(/_/g, " ")
                        .toLowerCase()
                        .replace(/\b\w/g, c => c.toUpperCase())),
        };
    }, [statut, typeSinistre, isSpecial, t]);


    /* Type de sinistre */
    const typeInfo = useMemo(() => {
        if (!typeSinistre) return null;
        return TYPE_SINISTRE[typeSinistre] ?? {
            label: typeSinistre,
            short: '-',
            color: BRAND.gray,
            bg: BRAND.grayBg,
            Icon: GlobalOutlined,
        };
    }, [typeSinistre]);


    /* Statut spécial */
    const specialInfo = isSpecial ? STATUTS_SPECIAUX[statut] : null;

    return (
        <div style={S.wrapper}>

            {/* ── En-tête ── */}
            <div style={S.header}>
                <div style={S.headerLeft}>
                    <span style={S.title}>{t('sinistreStatusStepper.suiviDossier')}</span>

                    {/* Badge statut */}
                    <span style={S.badge(statusColor, statusBg)}>
                        <span style={S.dot(statusColor)} />
                        {statusLabel}
                    </span>

                    {/* Badge type sinistre */}
                    {typeInfo && (() => {
                        const TypeIcon = typeInfo.Icon;
                        return (
                            <span style={S.typeBadge(typeInfo.color, typeInfo.bg)}>
                                <TypeIcon style={{ fontSize: 11 }} />
                                {typeInfo.label}
                            </span>
                        );
                    })()}

                    {isSpecial && (
                        <span style={{ fontSize: 11, color: BRAND.textMuted, fontStyle: 'italic' }}>
                            {t('sinistreStatusStepper.horsFluxStandard')}
                        </span>
                    )}
                </div>

                <span style={{ fontSize: 11, color: BRAND.textMuted }}>
                    {t('sinistreStatusStepper.declareLe', { date: formatDate(sinistre?.dateDeclaration) })}
                </span>
            </div>

            {/* ── Bannière statut spécial ── */}
            {isSpecial && specialInfo && (() => {
                const SpecialIcon = specialInfo.Icon;
                return (
                    <div style={S.specialBanner(specialInfo.color, specialInfo.bg)}>
                        <SpecialIcon style={{ fontSize: 16 }} />
                        <span>
                            <Trans
                                t={t}
                                i18nKey="sinistreStatusStepper.statutSpecial"
                                values={{ statut: specialInfo.label }}
                                components={{ 1: <strong /> }}
                            />
                        </span>
                    </div>
                );
            })()}

            {/* ── Stepper ── */}
            {!isSpecial && (
                <div style={S.trackOuter}>
                    <div style={S.trackInner}>
                        {PHASES.map((phase, pi) => (
                            <React.Fragment key={phase.key}>
                                <div style={S.phaseGroup}>
                                    {/* Label de phase */}
                                    <div style={S.phaseHeader(phase.color, phase.bg)}>
                                        {phase.label}
                                    </div>

                                    {/* Étapes */}
                                    <div style={S.phaseSteps}>
                                        {phase.steps.map((step, si) => {
                                            const gIdx = ALL_KEYS.indexOf(step.key);
                                            const isDone = gIdx < currentIdx;
                                            const isAct = gIdx === currentIdx;
                                            const state = isDone ? 'done' : isAct ? 'active' : 'pending';
                                            const isLast = pi === PHASES.length - 1 && si === phase.steps.length - 1;
                                            const StepIcon = step.Icon;

                                            return (
                                                <div key={step.key} style={S.stepItem}>
                                                    {!isLast && (
                                                        <div style={S.connector(isDone, phase.color)} />
                                                    )}
                                                    <div style={S.dotWrap(state, phase.color)}>
                                                        {isDone
                                                            ? <CheckOutlined style={{ fontSize: 11, color: '#fff' }} />
                                                            : <StepIcon style={{
                                                                fontSize: 13,
                                                                color: state !== 'pending' ? '#fff' : BRAND.textMuted,
                                                            }} />
                                                        }
                                                    </div>
                                                    <div style={S.stepLabel(state, phase.color)}>
                                                        {/* L'étape dit à qui la garantie est demandée,
                                                            et d'où le PV est attendu (TE : de l'homologue). */}
                                                        {step.key === 'GARANTIE_DEMANDEE' && typeSinistre
                                                            ? libelleStatutGarantie('GARANTIE_DEMANDEE', typeSinistre)
                                                            : step.key === 'ATTENTE_PV' && typeSinistre === 'SURVENU_ETRANGER'
                                                            ? t('sinistreStatusStepper.pvHomologue')
                                                            : step.label}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Séparateur inter-phases */}
                                {pi < PHASES.length - 1 && (
                                    <div style={S.phaseDivider} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}

            {/* La rangee de statistiques a ete retiree du bandeau de suivi.
                « Pays gestionnaire » figure deja dans l'onglet Informations, « Affecte a »
                a un onglet entier, et le bandeau doit donner l'AVANCEMENT du dossier -
                pas re-decrire le dossier lui-meme. Ce qui reste ici : la frise et rien
                d'autre. */}
        </div>
    );
}